import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

const NEAR_BOTTOM_THRESHOLD_PX = 70
const SMOOTH_SCROLL_DURATION_MS = 300

type UseStickToBottomOptions = {
  nearBottomThreshold?: number
}

type UseStickToBottomReturn = {
  scrollRef: React.RefCallback<HTMLElement>
  isAtBottom: boolean
  scrollToBottom: () => void
}

/**
 * @param contentKey - A value that changes whenever the scroll content changes.
 *   Must change even when items are swapped (e.g., typing indicator replaced by message).
 *   Use a string like `${messageCount}:${lastMessageId}:${isTyping}` to cover all cases.
 */
export function useStickToBottom(
  contentKey: string | number,
  options: UseStickToBottomOptions = {}
): UseStickToBottomReturn {
  const threshold = options.nearBottomThreshold ?? NEAR_BOTTOM_THRESHOLD_PX
  const [isAtBottom, setIsAtBottom] = useState(true)

  // Mutable refs for synchronous tracking (no renders, no timeouts)
  const elRef = useRef<HTMLElement | null>(null)
  const scrollTopRef = useRef(0)
  const isNearBottomRef = useRef(true)
  const ignoreNextScrollRef = useRef(false)
  const smoothScrollingRef = useRef(false)
  const animFrameRef = useRef(0)

  const computeNearBottom = useCallback(
    (el: HTMLElement) => {
      return el.scrollHeight - el.clientHeight - el.scrollTop < threshold
    },
    [threshold]
  )

  // --- Custom rAF smooth scroll ---
  // Recalculates target each frame so it always reaches the actual bottom,
  // even if content grows during the animation (typing indicator → message).
  const animateToBottom = useCallback((el: HTMLElement, duration = SMOOTH_SCROLL_DURATION_MS) => {
    cancelAnimationFrame(animFrameRef.current)
    const startScrollTop = el.scrollTop
    const startTime = performance.now()

    const tick = () => {
      const elapsed = performance.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      // Ease-out cubic: fast start, natural deceleration
      const eased = 1 - Math.pow(1 - progress, 3)
      // Recalculate target each frame — handles content growing during animation
      const target = el.scrollHeight - el.clientHeight
      el.scrollTop = startScrollTop + (target - startScrollTop) * eased
      scrollTopRef.current = el.scrollTop

      if (progress < 1 && smoothScrollingRef.current) {
        animFrameRef.current = requestAnimationFrame(tick)
      } else {
        el.scrollTop = el.scrollHeight - el.clientHeight
        scrollTopRef.current = el.scrollTop
        smoothScrollingRef.current = false
        isNearBottomRef.current = true
        setIsAtBottom(true)
      }
    }

    smoothScrollingRef.current = true
    animFrameRef.current = requestAnimationFrame(tick)
  }, [])

  const scrollToBottom = useCallback(() => {
    const el = elRef.current
    if (!el) return
    isNearBottomRef.current = true
    setIsAtBottom(true)
    animateToBottom(el)
  }, [animateToBottom])

  // --- Scroll listener: synchronous tracking ---
  const handleScroll = useCallback(() => {
    const el = elRef.current
    if (!el) return

    // Ignore single event from instant programmatic scroll (position restoration)
    if (ignoreNextScrollRef.current) {
      ignoreNextScrollRef.current = false
      scrollTopRef.current = el.scrollTop
      isNearBottomRef.current = computeNearBottom(el)
      return
    }

    // During smooth scroll animation: track position but don't flash the scroll button.
    // Clear once we've reached near-bottom (animation complete).
    if (smoothScrollingRef.current) {
      scrollTopRef.current = el.scrollTop
      if (computeNearBottom(el)) {
        smoothScrollingRef.current = false
        // Snap to exact bottom — native smooth scroll may stop short if content grew
        ignoreNextScrollRef.current = true
        el.scrollTop = el.scrollHeight - el.clientHeight
        scrollTopRef.current = el.scrollTop
        isNearBottomRef.current = true
        setIsAtBottom(true)
      }
      return
    }

    scrollTopRef.current = el.scrollTop
    const nearBottom = computeNearBottom(el)
    isNearBottomRef.current = nearBottom
    setIsAtBottom(nearBottom)
  }, [computeNearBottom])

  // --- Wheel escape: immediately stop auto-scroll on upward wheel ---
  const handleWheel = useCallback((e: WheelEvent) => {
    if (e.deltaY < 0) {
      cancelAnimationFrame(animFrameRef.current)
      smoothScrollingRef.current = false
      isNearBottomRef.current = false
      setIsAtBottom(false)
    }
  }, [])

  // --- Touch escape: stop auto-scroll when user touches the scroll area ---
  // On mobile/touch devices wheel events never fire, so touchstart is needed
  // to let the user break out of a smooth scroll animation.
  // Unlike handleWheel we don't update isNearBottomRef/isAtBottom here because
  // touchstart doesn't indicate direction — the subsequent scroll events will
  // flow through the normal handleScroll branch and update state correctly.
  const handleTouchStart = useCallback(() => {
    if (smoothScrollingRef.current) {
      cancelAnimationFrame(animFrameRef.current)
      smoothScrollingRef.current = false
    }
  }, [])

  // --- Ref callback: attach/detach event listeners ---
  const scrollRef = useCallback(
    (node: HTMLElement | null) => {
      const prev = elRef.current
      if (prev) {
        prev.removeEventListener('scroll', handleScroll)
        prev.removeEventListener('wheel', handleWheel)
        prev.removeEventListener('touchstart', handleTouchStart)
      }
      elRef.current = node
      if (node) {
        node.addEventListener('scroll', handleScroll, { passive: true })
        node.addEventListener('wheel', handleWheel, { passive: true })
        node.addEventListener('touchstart', handleTouchStart, { passive: true })
        scrollTopRef.current = node.scrollTop
      }
    },
    [handleScroll, handleWheel, handleTouchStart]
  )

  // --- Auto-scroll on content change (before paint) ---
  useLayoutEffect(() => {
    const el = elRef.current
    if (!el) return

    if (isNearBottomRef.current) {
      const target = el.scrollHeight - el.clientHeight
      const distance = target - el.scrollTop
      const shouldSmooth = distance > 0 && distance <= el.clientHeight && el.clientHeight > 0
      if (shouldSmooth) {
        smoothScrollingRef.current = true
        el.scrollTo({ top: target, behavior: 'smooth' })
        // Watchdog: for tiny distances the browser may complete the smooth scroll
        // in a single frame without firing scroll events, leaving smoothScrollingRef stuck.
        // Check on the next frame and clear if we've arrived.
        cancelAnimationFrame(animFrameRef.current)
        animFrameRef.current = requestAnimationFrame(() => {
          if (smoothScrollingRef.current && elRef.current && computeNearBottom(elRef.current)) {
            smoothScrollingRef.current = false
            isNearBottomRef.current = true
            setIsAtBottom(true)
          }
        })
      } else {
        // Large jump, hidden container (0 dimensions), or already at target — instant scroll.
        // Cancel any in-flight smooth scroll so it doesn't override this assignment.
        cancelAnimationFrame(animFrameRef.current)
        smoothScrollingRef.current = false
        el.scrollTop = target
      }
      scrollTopRef.current = target
      isNearBottomRef.current = true
    } else {
      // Explicitly restore scroll position — browser can reset scrollTop during DOM mutations
      ignoreNextScrollRef.current = true
      el.scrollTop = scrollTopRef.current
    }
  }, [contentKey, computeNearBottom])

  // --- ResizeObserver for content size changes (images loading, dynamic content) ---
  useEffect(() => {
    const el = elRef.current
    if (!el) return

    const content = el.firstElementChild
    if (!content) return

    const observer = new ResizeObserver(() => {
      if (!elRef.current) return
      // Don't interrupt an ongoing smooth scroll animation
      if (smoothScrollingRef.current) return
      if (isNearBottomRef.current) {
        ignoreNextScrollRef.current = true
        elRef.current.scrollTop = elRef.current.scrollHeight - elRef.current.clientHeight
        scrollTopRef.current = elRef.current.scrollTop
      } else {
        elRef.current.scrollTop = scrollTopRef.current
      }
    })

    observer.observe(content)

    return () => observer.disconnect()
  }, [])

  // --- Viewport resize (mobile keyboard open/close) ---
  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    const handleResize = () => {
      const el = elRef.current
      if (!el) return
      if (smoothScrollingRef.current) return
      if (isNearBottomRef.current) {
        ignoreNextScrollRef.current = true
        el.scrollTop = el.scrollHeight - el.clientHeight
        scrollTopRef.current = el.scrollTop
      }
    }

    viewport.addEventListener('resize', handleResize)
    return () => viewport.removeEventListener('resize', handleResize)
  }, [])

  // --- IntersectionObserver: scroll to bottom when container becomes visible (chat reopen) ---
  useEffect(() => {
    const el = elRef.current
    if (!el) return

    let wasHidden = false
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && wasHidden && isNearBottomRef.current) {
        el.scrollTop = el.scrollHeight - el.clientHeight
        scrollTopRef.current = el.scrollTop
      }
      wasHidden = !entries[0]?.isIntersecting
    })

    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // --- Cleanup animation on unmount ---
  useEffect(() => {
    return () => cancelAnimationFrame(animFrameRef.current)
  }, [])

  return { scrollRef, isAtBottom, scrollToBottom }
}
